import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// Check if we have stored auth data
const getStoredAuth = () => {
  try {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      const user = JSON.parse(userStr);
      return { user, token, isAuthenticated: true };
    }
  } catch (error) {
    console.error('Error loading auth data:', error);
  }
  
  return { user: null, token: null, isAuthenticated: false };
};

// Initial state
const initialState = {
  ...getStoredAuth(),
  isLoading: false,
  /**
   * Residencial que el SUPERADMINISTRADOR está mirando.
   *
   * Solo aplica a ese rol: para todos los demás, la residencial sale del
   * usuario en el servidor y esto queda en null. Es una preferencia de
   * pantalla, no un permiso — el backend ignora la cabecera salvo para el
   * superadministrador.
   */
  residentialSeleccionada: null,
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      ...initialState,

      // Login action
      login: (userData, token) => {
        // Update last login time
        const userWithLoginTime = {
          ...userData,
          lastLogin: new Date().toISOString(),
        };

        // Save to localStorage manually as well
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userWithLoginTime));

        set({
          user: userWithLoginTime,
          token,
          isAuthenticated: true,
          isLoading: false,
          // Un superadministrador entra sin residencial elegida; los demás
          // no eligen nada.
          residentialSeleccionada: null,
        });
      },

      // Logout action
      logout: () => {
        /*
         * Se limpia TODO lo del usuario anterior.
         *
         * Antes quedaban dos rastros: el estado persistido `auth-storage` y
         * las notificaciones guardadas en `notifications_<id>`. En un
         * dispositivo compartido —la tablet de la caseta, donde los guardias
         * rotan— el siguiente en entrar podía ver las notificaciones del
         * anterior. Con varias residenciales, incluso las de otra.
         */
        const idAnterior = get().user?.id || get().user?._id;

        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("auth-storage");
        if (idAnterior) localStorage.removeItem(`notifications_${idAnterior}`);

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          residentialSeleccionada: null,
        });
      },

      // Update user data
      updateUser: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData }
        }));
      },

      // Check if user has specific role
      hasRole: (role) => {
        const { user } = get();
        return user?.role === role;
      },

      // Check if user is resident
      isResident: () => get().hasRole('resident'),

      // Check if user is guard
      isGuard: () => get().hasRole('guard'),

      // Check if user is admin
      isAdmin: () => get().hasRole('admin'),

      // ¿Opera la plataforma entera?
      isSuperadmin: () => get().hasRole('superadmin'),

      /** La residencial del usuario: nombre, logo, color y polígonos. */
      residencial: () => get().user?.residential || null,

      /** El superadministrador elige en cuál trabaja. */
      seleccionarResidencial: (id) => set({ residentialSeleccionada: id || null }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        residentialSeleccionada: state.residentialSeleccionada,
      }),
      version: 2,
    }
  )
);

export default useAuthStore;